import { EditIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  GridItem,
  HStack,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Progress,
  Spinner,
  Switch,
  Text,
} from "@chakra-ui/react";

import { Grid, VStack } from "@chakra-ui/react";
import { FaInstagram, FaMousePointer, FaShoppingCart } from "react-icons/fa";
import { IoEllipsisVertical } from "react-icons/io5";
import AdsetImage from "./AdsetImage";
import { EventDetailManagedCampaignsSection } from "@common/interfaces/KYDEvent";
import { Step } from "./CreateCampaignWizard";

const calculatePercentage = (total: number, spent: number) => {
  if (!total || !spent) return 0;

  return (spent * 100) / total;
};

const CampaignList = ({
  campaigns,
  publishAdset,
  unpublishAdset,
  previewAd,
  setIsExpanded,
  isExpanded,
  loading,
  onAddAdset,
  setDefaultCampaign,
  setStep,
  setDefaultEditingAdset,
  onPublishCampaign,
  onUnpublishCampaign,
  loadingMessage,
  isLoggedIn,
}: {
  loadingMessage: (campaign_id: string) => string | null;
  campaigns: EventDetailManagedCampaignsSection["data"]["campaigns"];
  publishAdset: (params: any) => void;
  unpublishAdset: (params: any) => void;
  previewAd: (params: any) => void;
  setIsExpanded: (params: any) => void;
  isExpanded: any;
  loading: {
    publish: Record<string, boolean>;
    adsets: boolean;
  };
  setDefaultCampaign: (params: any) => void;
  setStep: (params: Step) => void;
  setDefaultEditingAdset: (params: any) => void;
  onPublishCampaign: (params: { id: string }) => Promise<any>;
  onUnpublishCampaign: (params: { id: string }) => Promise<any>;
  onAddAdset: (params: any) => void;
  isLoggedIn: boolean;
}) => {
  const shouldBeDisabled = (campaign: any) => {
    return !isLoggedIn;
  };

  const campaignHasError = (campaign: any) => {
    return campaign?.publish_errors?.length;
  };

  return (
    <VStack
      spacing={4}
      w="full"
      overflow="scroll"
      whiteSpace={["nowrap", "normal"]}
      minWidth={["max-content", "full"]}
    >
      <Grid
        w="full"
        templateColumns={[
          "0.2fr 0.8fr 0.6fr 0.3fr 0.3fr 0.3fr 0.3fr 0.3fr 0.3fr 0.3fr",
          "0.8fr 1.5fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1.5fr",
        ]}
        textTransform={"uppercase"}
        color="gray.600"
        fontSize={"small"}
        letterSpacing={"wider"}
        fontWeight={"bold"}
        px={2}
        gap={2}
        rowGap={0}
      >
        <GridItem></GridItem>
        <GridItem>Name</GridItem>
        <GridItem>Status</GridItem>
        <GridItem>ROAS</GridItem>
        <GridItem>Sales</GridItem>
        <GridItem>Budget</GridItem>
        <GridItem>CTR</GridItem>
        <GridItem>CPM</GridItem>
        <GridItem>Impress</GridItem>
        <GridItem justifySelf={"end"}>Actions</GridItem>
      </Grid>
      <VStack w="full">
        {campaigns.map((campaign) => (
          <Grid
            templateColumns={[
              "0.2fr 0.8fr 0.6fr 0.3fr 0.3fr 0.3fr 0.3fr 0.3fr 0.3fr 0.3fr",
              "0.8fr 1.5fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1.5fr",
            ]}
            key={campaign.id}
            cursor={"pointer"}
            onClick={() => {
              setIsExpanded((isExpanded) => ({
                ...isExpanded,
                [campaign.id]: !isExpanded[campaign.id],
              }));
            }}
            w="full"
            gap={2}
            py={2}
            px={2}
            rowGap={0}
            rounded="md"
            bg="gray.100"
          >
            <GridItem>
              <VStack h="full" alignItems={"center"} justifyContent={"center"}>
                <FaInstagram />
              </VStack>
            </GridItem>
            <GridItem alignSelf={"center"} wordBreak={"break-word"}>
              <Text fontWeight={"bold"}>{campaign.name}</Text>
            </GridItem>
            <GridItem alignSelf={"center"}>
              {campaignHasError(campaign) ? (
                <Badge colorScheme="red">Error publishing</Badge>
              ) : (
                <Badge
                  colorScheme={campaign.status === "ACTIVE" ? "green" : "gray"}
                >
                  <HStack gap={1}>
                    {campaign.campaign_type === "conversion" ? (
                      <FaShoppingCart fontSize={10} />
                    ) : (
                      <FaMousePointer />
                    )}
                    <Text>{campaign.status}</Text>
                  </HStack>
                </Badge>
              )}
            </GridItem>
            <GridItem alignSelf={"center"}>
              <Text>{campaign.metrics?.display_kyd_roas}</Text>
            </GridItem>
            <GridItem alignSelf={"center"}>
              <Text>{campaign.metrics?.display_ticket_gmv}</Text>
            </GridItem>
            <GridItem alignSelf={"center"}>
              {/* <Progress
                    size="md"
                    w="full"
                    rounded={"lg"}
                    colorScheme={"green"}
                    value={
                      campaign.metrics?.spend
                        ? campaign.metrics?.spend / campaign.lifetime_budget
                        : 0
                    }
                  /> */}
              <Text>{campaign.metrics?.display_spend}</Text>
            </GridItem>
            <GridItem alignSelf={"center"}>
              <Text>{campaign.metrics?.display_ctr}</Text>
            </GridItem>
            <GridItem alignSelf={"center"}>
              <Text>{campaign.metrics?.display_cpm}</Text>
            </GridItem>
            <GridItem alignSelf={"center"}>
              <Text>{campaign.metrics?.display_impressions}</Text>
            </GridItem>
            <GridItem>
              <HStack
                justifyContent={"flex-end"}
                alignItems={"center"}
                h="full"
              >
                {loading.publish[campaign.id] || campaign.execution_id ? (
                  <HStack px={4} gap={2} flexShrink={0}>
                    {loadingMessage && (
                      <Text fontSize={"xs"}>{loadingMessage(campaign.id)}</Text>
                    )}
                    <Spinner size="sm" />
                  </HStack>
                ) : (
                  <>
                    <IconButton
                      aria-label="Edit campaign"
                      icon={<EditIcon />}
                      variant="ghost"
                      isDisabled={shouldBeDisabled(campaign)}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDefaultCampaign(campaign);
                        setStep("edit_campaign");
                      }}
                    />
                    <Switch
                      isChecked={campaign.status === "ACTIVE"}
                      checked={campaign.status === "ACTIVE"}
                      isDisabled={
                        loading.adsets ||
                        loading.publish[campaign.id] ||
                        !!campaign.execution_id ||
                        shouldBeDisabled(campaign)
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                      onChange={(e) => {
                        if (campaign.status === "ACTIVE") {
                          onUnpublishCampaign({ id: campaign.id });
                        } else if (campaign.status === "PAUSED") {
                          onPublishCampaign({ id: campaign.id });
                        }
                      }}
                      colorScheme="green"
                    />
                  </>
                )}
              </HStack>
            </GridItem>
            <GridItem colSpan={10} w="full">
              {isExpanded[campaign.id] ? (
                <>
                  {campaign.adsets?.map((adset) => (
                    <Grid
                      templateColumns={[
                        "0.2fr 0.8fr 0.6fr 0.3fr 0.3fr 0.3fr 0.3fr 0.3fr 0.3fr 0.3fr",
                        "0.8fr 1.5fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1.5fr",
                      ]}
                      py={2}
                      key={adset.id}
                      w="full"
                      rowGap={0}
                      gap={2}
                      rounded="md"
                    >
                      <GridItem>
                        <VStack
                          h="full"
                          alignItems={"center"}
                          justifyContent={"center"}
                        >
                          {loading.publish[campaign.id] ||
                          campaign.execution_id ? (
                            <Spinner size="sm" />
                          ) : (
                            <AdsetImage adset={adset} />
                          )}
                        </VStack>
                      </GridItem>
                      <GridItem alignSelf={"center"}>
                        <Text fontSize={"xs"}>{adset.name}</Text>
                      </GridItem>
                      <GridItem alignSelf={"center"}>
                        <Badge
                          colorScheme={
                            adset.status === "ACTIVE" ? "green" : "gray"
                          }
                        >
                          {adset.status}
                        </Badge>
                      </GridItem>
                      <GridItem alignSelf={"center"}>
                        <Text fontSize={"xs"}>
                          {adset.metrics?.display_kyd_roas}
                        </Text>
                      </GridItem>
                      <GridItem alignSelf={"center"}>
                        <Text fontSize={"xs"}>
                          {adset.metrics?.display_ticket_gmv}
                        </Text>
                      </GridItem>
                      <GridItem alignSelf={"center"}>
                        <Progress
                          size="sm"
                          w="full"
                          rounded={"lg"}
                          colorScheme={"green"}
                          backgroundColor={"white"}
                          value={calculatePercentage(
                            adset.lifetime_budget.amount,
                            parseFloat(adset.metrics?.spend)
                          )}
                        />
                        <Text fontSize={"xs"}>
                          {adset.metrics?.display_spend} /{" "}
                          {adset.display_lifetime_budget}
                        </Text>
                      </GridItem>
                      <GridItem alignSelf={"center"}>
                        <Text fontSize={"xs"}>
                          {adset.metrics?.display_ctr}
                        </Text>
                      </GridItem>
                      <GridItem alignSelf={"center"}>
                        <Text fontSize={"xs"}>
                          {adset.metrics?.display_cpm}
                        </Text>
                      </GridItem>
                      <GridItem alignSelf={"center"}>
                        <Text fontSize={"xs"}>
                          {adset.metrics?.display_impressions}
                        </Text>
                      </GridItem>
                      <GridItem onClick={(e) => e.stopPropagation()}>
                        <HStack justify={"flex-end"} gap={0}>
                          <IconButton
                            aria-label="Edit adset"
                            icon={<EditIcon />}
                            isDisabled={
                              loading.adsets ||
                              loading.publish[campaign.id] ||
                              ["DELETED", "ARCHIVED"].includes(adset.status) ||
                              shouldBeDisabled(campaign)
                            }
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDefaultCampaign(campaign);
                              setDefaultEditingAdset(adset);
                              setStep("adset");
                            }}
                          />
                          {adset.external_id ? (
                            <Switch
                              isChecked={adset.status === "ACTIVE"}
                              checked={adset.status === "ACTIVE"}
                              isDisabled={
                                loading.adsets ||
                                loading.publish[campaign.id] ||
                                ["DELETED", "ARCHIVED"].includes(
                                  adset.status
                                ) ||
                                shouldBeDisabled(campaign)
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                              onChange={(e) => {
                                if (adset.status === "ACTIVE") {
                                  unpublishAdset({
                                    adset_id: adset.id,
                                    campaign_id: campaign.id,
                                    event_id: campaign.event_id,
                                  });
                                } else if (adset.status === "PAUSED") {
                                  publishAdset({
                                    adset_id: adset.id,
                                    campaign_id: campaign.id,
                                    event_id: campaign.event_id,
                                  });
                                }
                              }}
                              colorScheme="green"
                            />
                          ) : (
                            <Button
                              isLoading={loading.publish[campaign.id]}
                              isDisabled={
                                loading.adsets ||
                                loading.publish[campaign.id] ||
                                ["DELETED", "ARCHIVED"].includes(
                                  adset.status
                                ) ||
                                shouldBeDisabled(campaign)
                              }
                              colorScheme={"green"}
                              size={"xs"}
                              onClick={() => {
                                publishAdset({
                                  adset_id: adset.id,
                                  campaign_id: campaign.id,
                                  event_id: campaign.event_id,
                                });
                              }}
                            >
                              Publish
                            </Button>
                          )}

                          <Menu>
                            <MenuButton
                              as={IconButton}
                              isDisabled={
                                loading.adsets ||
                                loading.publish[campaign.id] ||
                                shouldBeDisabled(campaign)
                              }
                              variant={"ghost"}
                              icon={<IoEllipsisVertical />}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            />
                            <MenuList>
                              <MenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDefaultCampaign(campaign);
                                  setDefaultEditingAdset(adset);
                                  setStep("edit_adset");
                                }}
                                isDisabled={
                                  ["DELETED", "ARCHIVED"].includes(
                                    adset.status
                                  ) ||
                                  loading.adsets ||
                                  loading.publish[campaign.id] ||
                                  shouldBeDisabled(campaign)
                                }
                              >
                                Edit
                              </MenuItem>
                              <MenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  previewAd({
                                    adset_id: adset.id,
                                    campaign_id: campaign.id,
                                  });
                                }}
                              >
                                Preview
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        </HStack>
                      </GridItem>
                      {campaign.publish_errors?.length > 0 ? (
                        <GridItem colSpan={10}>
                          <HStack justifyContent={"flex-start"} py={2} px={2}>
                            <Text fontSize={"xs"} color="red.400">
                              {campaign.publish_errors}
                            </Text>
                          </HStack>
                        </GridItem>
                      ) : null}
                    </Grid>
                  ))}
                  <Button
                    flexGrow={1}
                    isDisabled={
                      shouldBeDisabled(campaign) ||
                      loading.adsets ||
                      loading.publish[campaign.id]
                    }
                    w="full"
                    onClick={() => onAddAdset(campaign)}
                  >
                    Add adset
                  </Button>
                </>
              ) : null}
              <GridItem colSpan={10} pt={3}></GridItem>
              {/* <Button onClick={() => previewAd(campaign.id)}>Preview</Button> */}
            </GridItem>
          </Grid>
        ))}
      </VStack>
    </VStack>
  );
};

export default CampaignList;
